import React, { useEffect, useState, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { Badge, Button, Card, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, LinkProps } from 'react-router-dom';
import pluralize from 'pluralize';
import { useEffectOnce } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';

import { selectIsFrontline } from '@components/Auth';
import { OrderableTypes, OrderStatuses } from '@models/Order';
import { CorporateUser, tGender, tUserRelationshipType, dUserName } from '@models/User';
import { formatDate } from '@utils/datetime';
import { AmountWithDiscount, ConfirmModal, DownloadLinkButton } from '@components/Base';
import { faPencil, faRemove, faRectangleList } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CorporateHealthOrder } from '@models/CorporateHealthOrder';
import { routeUserDashboardPolicyClaim } from '@components/UserDashboardRoute';

import { selectCorporateHealthQuoteBuy } from '../selector';
import {
  corporateHealthQuoteGetUsers,
  CorporateHealthQuoteGetUsersPayload,
  corporateHealthQuoteDeleteUser,
  CorporateHealthQuoteDeleteUserPayload,
  CorporateHealthQuoteUpdateUserPayload,
  corporateHealthQuoteUpdateUser,
  stateUpdate,
  StateUpdatePayload,
  corporateHealthQuoteExportActiveUsers,
  CorporateHealthQuoteExportActiveUsersPayload,
} from '../action';
import { UserControlForm, UserControlFormValues } from './user-control-form';
import { UserEditFormModal, UserEditFormValues } from './user-edit-form-modal';
import UserBenefitScheduleModal from './UserBenefitScheduleModal';

type Props = Pick<CorporateHealthOrder, 'orderNumber' | 'status' | 'activeUsersCsv'> & {
  claimable?: CorporateHealthOrder['claimable'];
  to?: LinkProps['to'];
  id?: CorporateHealthOrder['id'];
  remainingBenefits?: CorporateHealthOrder['remainingBenefits'];
};

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const UserCardBody = ({ id, orderNumber, status, claimable, remainingBenefits, activeUsersCsv }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isFrontline = useSelector(selectIsFrontline);
  const { users, isUsersLoading, usersPagination } = useSelector(selectCorporateHealthQuoteBuy);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [controlValues, setControlValues] = useState<UserControlFormValues>({ search: '' });
  const [selectedUser, setSelectedUser] = useState<CorporateUser | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showBenefitScheduleDialog, setShowBenefitScheduleDialog] = useState(false);

  const isPending = status === OrderStatuses.Pending;
  const isLocked = status === OrderStatuses.Locked;
  const isActive = status === OrderStatuses.Active;
  const shouldEdit = isFrontline && (isPending || isLocked || isActive);
  const shouldExport = isFrontline && (isPending || isLocked || isActive);
  const shouldDelete = isFrontline && isPending;
  const shouldShowBenefitSchedule = isFrontline && !isEmpty(remainingBenefits);

  const columns = useMemo(() => [
    {
      name: t('user.username'),
      selector: (user) => user.username,
      minWidth: '120px',
    },
    {
      name: t('common:family'),
      selector: (user) => user.familyFullName,
      minWidth: '120px',
    },
    {
      name: t('user.name'),
      selector: (user) => dUserName(user),
      minWidth: '120px',
    },
    {
      name: t('user.relationshipType'),
      selector: (user) => tUserRelationshipType(user.relationshipType)
    },
    {
      name: t('user.dob'),
      selector: (user) => formatDate(user.dob),
      width: '120px',
    },
    {
      name: t('user.gender'),
      selector: (user) => tGender(user.gender)
    },
    {
      name: t('common:status'),
      selector: (user) => (
        <Badge bg={user.active ? 'success' : 'secondary'}>
          {user.active ? t('common:active') : t('common:inactive')}
        </Badge>
      )
    },
    {
      name: t('common:memberSince'),
      selector: (user) => formatDate(user.activeDate),
      width: '120px',
    },
    {
      name: t('common:startDate'),
      selector: (user) => formatDate(user.startedAt),
      width: '120px',
    },
    {
      name: t('common:endDate'),
      selector: (user) => formatDate(user.endedAt),
      width: '120px',
    },
    {
      name: t('common:premium'),
      selector: (user) => <AmountWithDiscount skipIncluded finalAmount={user.premium} />
    },
    {
      name: t('common:refundedPremium'),
      selector: (user) => <AmountWithDiscount skipIncluded finalAmount={user.refundedPremium} />
    },
    {
      name: t('common:totalPremium'),
      selector: (user) => <AmountWithDiscount skipIncluded finalAmount={user.totalPremium} />
    },
    {
      cell: (user) => shouldEdit && (
        <Button onClick={() => handleEdit(user)} variant="outline-primary">
          <FontAwesomeIcon icon={faPencil} />
        </Button>
      ),
      width: '44px',
      button: 'true',
      center: 'true',
    },
    {
      cell: (user) => shouldDelete && user.familyCount === 0 && (
        <Button onClick={() => handleDelete(user)} variant="outline-danger">
          <FontAwesomeIcon icon={faRemove} />
        </Button>
      ),
      width: '44px',
      button: 'true',
      center: 'true',
    },
    {
      cell: (user) => shouldShowBenefitSchedule && (
        <Button onClick={() => handleShowBenefitSchedule(user)} variant="outline-primary">
          <FontAwesomeIcon icon={faRectangleList} />
        </Button>
      ),
      width: '44px',
      button: 'true',
      center: 'true',
    },
    {
      cell: (user) => claimable && user.active && (
        <Button
          as={Link}
          to={routeUserDashboardPolicyClaim(
            { orderableType: OrderableTypes.CorporateHealth, orderableId: Number(id) },
            { username: user.username },
          )}
          variant="outline-primary"
          size="sm"
        >
          {t('common:claim')}
        </Button>
      ),
      width: '44px',
      button: 'true',
      center: 'true',
    },
  ] as TableColumn<CorporateUser>[], [shouldEdit, shouldDelete, shouldShowBenefitSchedule, claimable]);

  const handleDelete = (user: CorporateUser) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleEdit = (user: CorporateUser) => {
    setSelectedUser(user);
    setShowEditDialog(true);
  };

  const handleShowBenefitSchedule = (user: CorporateUser) => {
    setSelectedUser(user);
    setShowBenefitScheduleDialog(true);
  };

  useEffect(() => {
    if (!orderNumber) return;
    const payload: CorporateHealthQuoteGetUsersPayload = { orderNumber, page, perPage, ...controlValues };
    dispatch(corporateHealthQuoteGetUsers(payload));
  }, [page, perPage, orderNumber, controlValues]);

  useEffectOnce(() => {
    return () => {
      const payload: StateUpdatePayload = { users: [] };
      dispatch(stateUpdate(payload));
    };
  });

  return (
    <>
      <Card.Body>
        <Card.Title className="d-flex flex-row justify-content-between align-items-center">
          {pluralize(t('common:user'), 2)} ({usersPagination?.total})
          <Stack direction="horizontal" gap={2}>
            {shouldExport && (
              <>
                <DownloadLinkButton href={activeUsersCsv} filename />
                <Button onClick={() => setShowExportDialog(true)} variant="outline-primary">
                  Export active users
                </Button>
              </>
            )}
          </Stack>
        </Card.Title>

        <UserControlForm
          initialValues={controlValues}
          onSubmit={(values: UserControlFormValues) => {
            setPage(1);
            setControlValues(values);
          }}
        />

        <DataTable
          columns={columns}
          data={users ?? []}
          customStyles={customStyles}
          fixedHeader
          pagination
          paginationServer
          progressPending={isUsersLoading}
          paginationPerPage={perPage}
          paginationTotalRows={usersPagination?.total ?? 0}
          onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
          onChangePage={(newPage) => setPage(newPage)}
        />
      </Card.Body>

      {shouldExport && (
        <ConfirmModal
          show={showExportDialog}
          onHide={() => {
            setShowExportDialog(false);
          }}
          onConfirm={() => {
            const payload: CorporateHealthQuoteExportActiveUsersPayload = { orderNumber };
            dispatch(corporateHealthQuoteExportActiveUsers(payload));
            setShowExportDialog(false);
          }}
        />
      )}

      {shouldDelete && (
        <ConfirmModal
          show={showDeleteDialog}
          onHide={() => {
            setSelectedUser(null);
            setShowDeleteDialog(false);
          }}
          onConfirm={() => {
            if (!selectedUser) return;
            const payload: CorporateHealthQuoteDeleteUserPayload = { orderNumber, userId: selectedUser.id };
            dispatch(corporateHealthQuoteDeleteUser(payload));
            setSelectedUser(null);
            setShowDeleteDialog(false);
          }}
        />
      )}

      {shouldEdit && (
        <UserEditFormModal
          user={selectedUser}
          show={showEditDialog}
          onHide={() => {
            setSelectedUser(null);
            setShowEditDialog(false);
          }}
          onSubmit={(values: UserEditFormValues) => {
            if (!selectedUser) return;
            const payload: CorporateHealthQuoteUpdateUserPayload = { orderNumber, userId: selectedUser.id, ...values };
            dispatch(corporateHealthQuoteUpdateUser(payload));
            setSelectedUser(null);
            setShowEditDialog(false);
          }}
        />
      )}

      {showBenefitScheduleDialog && selectedUser && (
        <UserBenefitScheduleModal
          {...{ id: Number(id), user: selectedUser }}
          show={showBenefitScheduleDialog}
          onHide={() => {
            setSelectedUser(null);
            setShowBenefitScheduleDialog(false);
          }}
        />
      )}
    </>
  );
};

export { UserCardBody };
